import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import {
  Button,
  KPICard,
  Card,
  ActionToolbar,
  DataNavigator,
  StatusBadge,
  Badge
} from '@/components/ui';
import { useAssets } from './hooks/useAssets';
import { AssetFormModal } from './components/AssetFormModal';
import { AssetImportModal } from './components/AssetImportModal';
import { AssetMapView } from './components/AssetMapView';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useZones } from '@/modules/maintenance/zones/hooks/useMaintenanceZones';
import type { MaintenanceAsset } from './types/assets.types';
import { toast } from 'sonner';
import {
  Plus,
  FileSpreadsheet,
  Table as TableIcon,
  Map as MapIcon,
  Search,
  HeartPulse,
  Sprout,
  AlertTriangle,
  Skull,
  Eye,
  Edit,
  Trash2,
  MapPin,
  Layers
} from 'lucide-react';

export default function AssetsPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editAsset, setEditAsset] = useState<MaintenanceAsset | null>(null);
  const [deleteAsset, setDeleteAsset] = useState<MaintenanceAsset | null>(null);

  const { projects = [] } = useProjects(companyId);
  const { zones = [] } = useZones(companyId);

  const {
    assets,
    stats,
    isLoading,
    filters,
    setFilters,
    deleteAsset: executeDelete,
    isDeleting,
  } = useAssets(companyId);

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleDelete = async () => {
    if (!deleteAsset) return;
    try {
      await executeDelete(deleteAsset.id);
      setDeleteAsset(null);
      toast.success('Đã xóa tài sản thành công');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'asset_code',
      title: 'Mã & Tên tài萨',
      render: (code: string, item: MaintenanceAsset) => (
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400  tracking-widest">{code}</span>
          <span
            className="font-black text-slate-900 hover:text-blue-600 cursor-pointer transition-colors leading-tight"
            onClick={() => navigate(`/maintenance/assets/detail/${item.id}`)}
          >
            {item.asset_name}
          </span>
        </div>
      )
    },
    {
      key: 'asset_type_code',
      title: 'Phân loại',
      render: (type: string) => (
        <Badge variant="info" className="gap-1.5">
          <Layers className="w-3 h-3" />
          {type}
        </Badge>
      )
    },
    {
      key: 'location',
      title: 'Khu vực / Dự án',
      render: (_: any, item: MaintenanceAsset) => (
        <div className="flex flex-col">
          <span className="text-xs font-bold text-slate-700">{item.zone?.zone_name || 'N/A'}</span>
          <span className="text-[10px] text-slate-400 font-black  tracking-tighter">{item.project?.code}</span>
        </div>
      )
    },
    {
      key: 'health_status',
      title: 'Sức khỏe',
      render: (status: string) => (
        <StatusBadge
          status={status}
          type="default"
          className="font-black"
        />
      )
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, item: MaintenanceAsset) => (
        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/maintenance/assets/detail/${item.id}`)}>
            <Eye className="w-4 h-4" />
          </Button>
          <AuthButton
            module="maintenance"
            action="update"
            variant="ghost"
            size="sm"
            onClick={() => setEditAsset(item)}
          >
            <Edit className="w-4 h-4" />
          </AuthButton>
          <AuthButton
            module="maintenance"
            action="delete"
            variant="ghost"
            size="sm"
            className="text-red-500 hover:bg-red-50"
            onClick={() => setDeleteAsset(item)}
          >
            <Trash2 className="w-4 h-4" />
          </AuthButton>
        </div>
      )
    }
  ], [navigate]);

  const mobileCardRender = (item: MaintenanceAsset) => (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400  tracking-widest">{item.asset_code}</span>
          <h3 className="font-black text-slate-900 text-lg leading-tight">{item.asset_name}</h3>
        </div>
        <StatusBadge status={item.health_status} type="default" size="sm" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 p-2 rounded-2xl bg-slate-50/50 border border-slate-100">
          <MapPin className="w-4 h-4 text-slate-400" />
          <div className="flex flex-col">
            <span className="text-[8px] font-black text-slate-400 ">Khu vực</span>
            <span className="text-xs font-bold text-slate-700 truncate">{item.zone?.zone_name || 'N/A'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 p-2 rounded-2xl bg-slate-50/50 border border-slate-100">
          <Layers className="w-4 h-4 text-slate-400" />
          <div className="flex flex-col">
            <span className="text-[8px] font-black text-slate-400 ">Phân loại</span>
            <span className="text-xs font-bold text-slate-700 truncate">{item.asset_type_code}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100/50">
        <div className="text-[10px] text-slate-500 font-bold italic">
          {item.species || 'Chưa gán chủng loại'}
        </div>
        <div className="flex gap-2">
           <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditAsset(item)}>
             <Edit className="w-4 h-4" />
           </Button>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Quản lý Tài sản"
      description="Hệ thống quản lý hạ tầng cây xanh & cảnh quan cao cấp"
      breadcrumbs={[{ label: 'Bảo trì' }, { label: 'Tài sản' }]}
    >
      <div className="space-y-6">
        {/* KPI Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng số tài sản"
            value={stats.total}
            icon={Sprout}
            variant="primary"
          />
          <KPICard
            title="Sức khỏe tốt"
            value={stats.goodHealth}
            icon={HeartPulse}
            variant="success"
          />
          <KPICard
            title="Cần chú ý"
            value={stats.warningHealth}
            icon={AlertTriangle}
            variant="warning"
          />
          <KPICard
            title="Vấn đề"
            value={stats.badHealth}
            icon={Skull}
            variant="danger"
          />
        </div>

        {/* Action Toolbar */}
        <ActionToolbar
          searchValue={filters.search}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Tìm kiếm tài sản, mã cây..."
          filterOptions={[
            {
              id: 'projectId', label: 'Dự án', type: 'select', choices: [
                { value: 'all', label: 'Tất cả dự án' },
                ...projects.map(p => ({ value: p.id, label: p.name }))
              ]
            },
            {
              id: 'zoneId', label: 'Khu vực', type: 'select', choices: [
                { value: 'all', label: 'Tất cả khu vực' },
                ...zones.filter(z => !filters.projectId || filters.projectId === 'all' || z.project_id === filters.projectId)
                  .map(z => ({ value: z.id, label: z.zone_name }))
              ]
            },
            {
              id: 'health', label: 'Sức khỏe', type: 'select', choices: [
                { value: 'all', label: 'Tất cả' },
                { value: 'good', label: 'Khỏe mạnh' },
                { value: 'warning', label: 'Cần chú ý' },
                { value: 'bad', label: 'Vấn đề' }
              ]
            }
          ]}
          onFilterChange={handleFilterChange}
          actions={
            <div className="flex gap-2 mr-2">
              <Button variant="outline" size="md" onClick={() => setShowImport(true)} className="rounded-2xl">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Import
              </Button>
              <div className="flex bg-slate-100/50 p-1 rounded-2xl border border-slate-200/50">
                <Button
                  variant={viewMode === 'table' ? 'primary' : 'ghost'}
                  size="sm"
                  className="rounded-xl px-3 h-8"
                  onClick={() => setViewMode('table')}
                >
                  <TableIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'primary' : 'ghost'}
                  size="sm"
                  className="rounded-xl px-3 h-8"
                  onClick={() => setViewMode('map')}
                >
                  <MapIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          }
          primaryActionLabel="Thêm tài sản"
          primaryActionIcon={<Plus className="w-4 h-4" />}
          onPrimaryAction={() => setShowCreate(true)}
          isLoading={isLoading}
        />

        {/* Data Navigator */}
        {viewMode === 'table' ? (
          <DataNavigator
            columns={columns}
            data={assets}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`/maintenance/assets/detail/${item.id}`)}
            mobileCardRender={mobileCardRender}
          />
        ) : (
          <Card variant="glass" className="overflow-hidden h-[600px]">
            <AssetMapView assets={assets} />
          </Card>
        )}
      </div>

      <AssetFormModal
        isOpen={showCreate || !!editAsset}
        asset={editAsset}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditAsset(null);
        }}
      />

      <AssetImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImport={async () => {}}
      />

      <ConfirmDialog
        isOpen={!!deleteAsset}
        title="Xác nhận xóa tài sản"
        message={`Bạn có chắc chắn muốn xóa tài sản "${deleteAsset?.asset_name}"?`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteAsset(null)}
      />
    </PageLayout>
  );
}
